import { useState, useCallback } from 'react';
import { ethers } from 'ethers';
import {
  BONDING_CURVE_ADDRESS,
  BONDING_CURVE_ABI,
  ERC20_ABI,
  USDT_ADDRESS,
  MIGRATION_THRESHOLD,
  DUSD_ADDRESS,
  ADD_TOKEN_FEE,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ADD_TOKEN_FEE_RECEIVER,
} from '../config/contracts';

export interface TokenData {
  tokenAddress: string;
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  reserveUSDT: string;
  tokenBalance: string;
  currentPrice: string;
  migrated: boolean;
  active: boolean;
  creator: string;
  createdAt: number;
  progress: number; // 0-100
  description: string;
  website: string;
}

export function useBondingCurve(signer: ethers.Signer | null, provider: ethers.providers.Web3Provider | null) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getContract = useCallback((readOnly = false) => {
    if (!BONDING_CURVE_ADDRESS) return null;
    const signerOrProvider = readOnly
      ? (provider || new ethers.providers.JsonRpcProvider('https://bsc-dataseed1.binance.org/'))
      : signer;
    if (!signerOrProvider) return null;
    return new ethers.Contract(BONDING_CURVE_ADDRESS, BONDING_CURVE_ABI, signerOrProvider);
  }, [signer, provider]);

  const getUSDTContract = useCallback((readOnly = false) => {
    const signerOrProvider = readOnly
      ? (provider || new ethers.providers.JsonRpcProvider('https://bsc-dataseed1.binance.org/'))
      : signer;
    if (!signerOrProvider) return null;
    return new ethers.Contract(USDT_ADDRESS, ERC20_ABI, signerOrProvider);
  }, [signer, provider]);

  const getTokenERC20 = useCallback((tokenAddress: string, readOnly = false) => {
    const signerOrProvider = readOnly
      ? (provider || new ethers.providers.JsonRpcProvider('https://bsc-dataseed1.binance.org/'))
      : signer;
    if (!signerOrProvider) return null;
    return new ethers.Contract(tokenAddress, ERC20_ABI, signerOrProvider);
  }, [signer, provider]);

  const parseTokenData = (raw: any): TokenData => {
    const reserveUSDT = parseFloat(ethers.utils.formatUnits(raw.reserveUSDT, 18));
    const threshold = parseFloat(MIGRATION_THRESHOLD);
    const progress = Math.min((reserveUSDT / threshold) * 100, 100);

    return {
      tokenAddress: raw.tokenAddress,
      name: raw.name,
      symbol: raw.symbol,
      decimals: raw.decimals,
      totalSupply: ethers.utils.formatUnits(raw.totalSupply, raw.decimals),
      reserveUSDT: reserveUSDT.toFixed(2),
      tokenBalance: ethers.utils.formatUnits(raw.tokenBalance, raw.decimals),
      currentPrice: parseFloat(ethers.utils.formatUnits(raw.currentPrice, 18)).toFixed(8),
      migrated: raw.migrated,
      active: raw.active,
      creator: raw.creator,
      createdAt: raw.createdAt.toNumber(),
      progress,
      description: raw.description || '',
      website: raw.website || '',
    };
  };

  // 获取所有代币
  const getAllTokens = useCallback(async (): Promise<TokenData[]> => {
    const contract = getContract(true);
    if (!contract) return [];
    try {
      const addresses: string[] = await contract.getAllTokens();
      const tokenDataList = await Promise.all(
        addresses.map(async (addr) => {
          const raw = await contract.getTokenInfo(addr);
          return parseTokenData(raw);
        })
      );
      return tokenDataList;
    } catch (e) {
      console.error('getAllTokens error:', e);
      return [];
    }
  }, [getContract]);

  // 获取单个代币信息
  const getTokenInfo = useCallback(async (tokenAddress: string): Promise<TokenData | null> => {
    const contract = getContract(true);
    if (!contract) return null;
    try {
      const raw = await contract.getTokenInfo(tokenAddress);
      return parseTokenData(raw);
    } catch (e) {
      return null;
    }
  }, [getContract]);

  // 添加代币（合约内部自动收取 10000 DUSD 上币费）
  const addToken = useCallback(async (
    tokenAddress: string,
    initialAmount: string,
    description: string = '',
    website: string = ''
  ): Promise<boolean> => {
    const contract = getContract(false);
    if (!contract || !signer) return false;

    setLoading(true);
    setError(null);

    try {
      const userAddress = await signer.getAddress();

      // Step 1: 检查 DUSD 余额是否足够
      const dusdContract = new ethers.Contract(DUSD_ADDRESS, ERC20_ABI, signer);
      const feeBN = ethers.utils.parseUnits(ADD_TOKEN_FEE, 18);
      const dusdBalance: ethers.BigNumber = await dusdContract.balanceOf(userAddress);
      if (dusdBalance.lt(feeBN)) {
        const has = parseFloat(ethers.utils.formatUnits(dusdBalance, 18)).toFixed(2);
        throw new Error(`DUSD 余额不足，需要 10,000 DUSD，当前余额 ${has} DUSD`);
      }

      // Step 2: Approve DUSD 给曲线合约（合约内部会 transferFrom）
      const dusdAllowance: ethers.BigNumber = await dusdContract.allowance(userAddress, BONDING_CURVE_ADDRESS);
      if (dusdAllowance.lt(feeBN)) {
        const approveDusdTx = await dusdContract.approve(BONDING_CURVE_ADDRESS, feeBN);
        await approveDusdTx.wait();
      }

      // Step 3: Approve 初始代币给曲线合约
      const tokenContract = getTokenERC20(tokenAddress, false);
      if (!tokenContract) throw new Error('无法连接代币合约');
      const decimals = await tokenContract.decimals();
      const amountBN = ethers.utils.parseUnits(initialAmount, decimals);

      // 检查初始代币余额
      const tokenBalance: ethers.BigNumber = await tokenContract.balanceOf(userAddress);
      if (tokenBalance.lt(amountBN)) {
        const has = parseFloat(ethers.utils.formatUnits(tokenBalance, decimals)).toFixed(4);
        const need = parseFloat(initialAmount).toFixed(4);
        throw new Error(`代币余额不足，需要 ${need}，当前余额 ${has}`);
      }

      const approveTx = await tokenContract.approve(BONDING_CURVE_ADDRESS, amountBN);
      await approveTx.wait();

      // Step 4: 添加代币到曲线（合约内自动扣除 DUSD 费用）
      const tx = await contract.addToken(tokenAddress, amountBN, description, website);
      await tx.wait();

      return true;
    } catch (e: any) {
      const msg: string = e.message || '';
      if (msg.includes('DUSD 余额不足') || msg.includes('代币余额不足') || msg.includes('无法连接')) {
        setError(msg);
      } else if (e.code === 4001 || msg.toLowerCase().includes('user rejected') || msg.toLowerCase().includes('user denied')) {
        setError('用户取消了交易');
      } else if (msg.toLowerCase().includes('insufficient funds')) {
        setError('BNB 不足，无法支付 Gas 费');
      } else if (msg.toLowerCase().includes('listing fee')) {
        setError('DUSD 上币费支付失败，请检查余额和授权');
      } else {
        setError(e.reason || '添加失败，请重试');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [getContract, getTokenERC20, signer]);

  // 购买代币
  const buyTokens = useCallback(async (
    tokenAddress: string,
    usdtAmount: string,
    slippage: number = 2
  ): Promise<boolean> => {
    const contract = getContract(false);
    const usdtContract = getUSDTContract(false);
    if (!contract || !usdtContract || !signer) return false;

    setLoading(true);
    setError(null);

    try {
      const usdtBN = ethers.utils.parseUnits(usdtAmount, 18);
      const userAddress = await signer.getAddress();

      // 前置校验：检查用户 USDT 余额是否足够
      const userUSDTBal: ethers.BigNumber = await usdtContract.balanceOf(userAddress);
      if (userUSDTBal.lt(usdtBN)) {
        const has = parseFloat(ethers.utils.formatUnits(userUSDTBal, 18)).toFixed(2);
        const need = parseFloat(usdtAmount).toFixed(2);
        throw new Error(`USDT 余额不足，需要 ${need}，当前余额 ${has}`);
      }

      // 查询预期获得的代币
      const expectedTokens = await contract.getBuyPrice(tokenAddress, usdtBN);
      if (expectedTokens.isZero()) {
        throw new Error('购买金额过小，预期获得代币为 0');
      }
      const minTokenOut = expectedTokens.mul(100 - slippage).div(100);

      // Approve USDT（检查已有授权，避免重复授权）
      const currentAllowance: ethers.BigNumber = await usdtContract.allowance(userAddress, BONDING_CURVE_ADDRESS);
      if (currentAllowance.lt(usdtBN)) {
        const approveTx = await usdtContract.approve(BONDING_CURVE_ADDRESS, usdtBN);
        await approveTx.wait();
      }

      // 购买
      const tx = await contract.buyTokens(tokenAddress, usdtBN, minTokenOut);
      await tx.wait();

      return true;
    } catch (e: any) {
      const msg: string = e.message || '';
      if (msg.includes('USDT 余额不足') || msg.includes('购买金额过小')) {
        setError(msg);
      } else if (e.code === 4001 || msg.toLowerCase().includes('user rejected') || msg.toLowerCase().includes('user denied')) {
        setError('用户取消了交易');
      } else if (msg.toLowerCase().includes('insufficient funds')) {
        setError('BNB 不足，无法支付 Gas 费');
      } else if (msg.toLowerCase().includes('insufficient token liquidity')) {
        setError('代币流动性不足，请减少购买数量');
      } else if (msg.toLowerCase().includes('slippage')) {
        setError('滑点过大，请提高滑点容忍或减少购买数量');
      } else {
        setError(e.reason || '购买失败，请重试');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [getContract, getUSDTContract, signer]);

  // 出售代币
  const sellTokens = useCallback(async (
    tokenAddress: string,
    tokenAmount: string,
    decimals: number,
    slippage: number = 2
  ): Promise<boolean> => {
    const contract = getContract(false);
    if (!contract || !signer) return false;

    setLoading(true);
    setError(null);

    try {
      const tokenBN = ethers.utils.parseUnits(tokenAmount, decimals);
      const tokenContract = getTokenERC20(tokenAddress, false);
      if (!tokenContract) throw new Error('合约连接失败');

      const userAddress = await signer.getAddress();

      // 前置校验：检查用户代币余额是否足够
      const userTokenBal: ethers.BigNumber = await tokenContract.balanceOf(userAddress);
      if (userTokenBal.lt(tokenBN)) {
        const has = parseFloat(ethers.utils.formatUnits(userTokenBal, decimals)).toFixed(4);
        const need = parseFloat(tokenAmount).toFixed(4);
        throw new Error(`代币余额不足，需要 ${need}，当前余额 ${has}`);
      }

      // 前置校验：查询预期获得的 USDT，确认储备充足
      const expectedUSDT = await contract.getSellPrice(tokenAddress, tokenBN);
      if (expectedUSDT.isZero()) {
        throw new Error('卖出金额过小，预期获得 USDT 为 0');
      }
      const minUSDTOut = expectedUSDT.mul(100 - slippage).div(100);

      // Approve 代币给曲线合约
      const currentAllowance: ethers.BigNumber = await tokenContract.allowance(userAddress, BONDING_CURVE_ADDRESS);
      if (currentAllowance.lt(tokenBN)) {
        const approveTx = await tokenContract.approve(BONDING_CURVE_ADDRESS, tokenBN);
        await approveTx.wait();
      }

      // 出售
      const tx = await contract.sellTokens(tokenAddress, tokenBN, minUSDTOut);
      await tx.wait();

      return true;
    } catch (e: any) {
      const msg: string = e.message || '';
      if (msg.includes('代币余额不足') || msg.includes('卖出金额过小') || msg.includes('合约连接失败')) {
        setError(msg);
      } else if (e.code === 4001 || msg.toLowerCase().includes('user rejected') || msg.toLowerCase().includes('user denied')) {
        setError('用户取消了交易');
      } else if (msg.toLowerCase().includes('insufficient funds')) {
        setError('BNB 不足，无法支付 Gas 费');
      } else if (msg.toLowerCase().includes('insufficient balance') || msg.toLowerCase().includes('insufficient usdt reserve')) {
        setError('合约 USDT 储备不足，无法完成卖出');
      } else if (msg.toLowerCase().includes('slippage')) {
        setError('滑点过大，请提高滑点容忍或减少卖出数量');
      } else {
        setError(e.reason || '出售失败，请重试');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [getContract, getTokenERC20, signer]);

  // 手动触发迁移
  const migrateToken = useCallback(async (tokenAddress: string): Promise<boolean> => {
    const contract = getContract(false);
    if (!contract || !signer) return false;

    setLoading(true);
    setError(null);

    try {
      const tx = await contract.migrateToPancakeSwap(tokenAddress);
      await tx.wait();
      return true;
    } catch (e: any) {
      setError(e.reason || e.message || '迁移失败');
      return false;
    } finally {
      setLoading(false);
    }
  }, [getContract, signer]);

  // 获取用户 USDT 余额
  const getUSDTBalance = useCallback(async (account: string): Promise<string> => {
    const contract = getUSDTContract(true);
    if (!contract) return '0';
    try {
      const bal = await contract.balanceOf(account);
      return parseFloat(ethers.utils.formatUnits(bal, 18)).toFixed(2);
    } catch {
      return '0';
    }
  }, [getUSDTContract]);

  // 获取用户代币余额
  const getTokenBalance = useCallback(async (
    tokenAddress: string,
    account: string,
    decimals: number
  ): Promise<string> => {
    const contract = getTokenERC20(tokenAddress, true);
    if (!contract) return '0';
    try {
      const bal = await contract.balanceOf(account);
      return parseFloat(ethers.utils.formatUnits(bal, decimals)).toFixed(4);
    } catch {
      return '0';
    }
  }, [getTokenERC20]);

  // 预估购买
  const estimateBuy = useCallback(async (
    tokenAddress: string,
    usdtAmount: string,
    tokenDecimals: number
  ): Promise<string> => {
    const contract = getContract(true);
    if (!contract || !usdtAmount || parseFloat(usdtAmount) <= 0) return '0';
    try {
      const usdtBN = ethers.utils.parseUnits(usdtAmount, 18);
      const result = await contract.getBuyPrice(tokenAddress, usdtBN);
      return ethers.utils.formatUnits(result, tokenDecimals);
    } catch {
      return '0';
    }
  }, [getContract]);

  // 预估出售
  const estimateSell = useCallback(async (
    tokenAddress: string,
    tokenAmount: string,
    decimals: number
  ): Promise<string> => {
    const contract = getContract(true);
    if (!contract || !tokenAmount || parseFloat(tokenAmount) <= 0) return '0';
    try {
      const tokenBN = ethers.utils.parseUnits(tokenAmount, decimals);
      const result = await contract.getSellPrice(tokenAddress, tokenBN);
      return parseFloat(ethers.utils.formatUnits(result, 18)).toFixed(4);
    } catch {
      return '0';
    }
  }, [getContract]);

  return {
    loading,
    error,
    getAllTokens,
    getTokenInfo,
    addToken,
    buyTokens,
    sellTokens,
    migrateToken,
    getUSDTBalance,
    getTokenBalance,
    estimateBuy,
    estimateSell,
  };
}
